/**
 * Invoice create-wizard drafts — Sattva Backend is the source of truth.
 *
 * Contract: backend-handoff/INVOICE_TEMPLATES_API_DOCUMENTATION.md §11
 *   GET    /invoice/drafts/current
 *   PUT    /invoice/drafts/current   (upsert)
 *   DELETE /invoice/drafts/current
 *
 * If those routes are not implemented yet (404/400/501/network), a session
 * in-memory copy is used for the rest of this page load only — never
 * written to localStorage. Callers should toast when `_source === 'session'`.
 */

import axios from 'axios'
import AuthService from './auth-header'
import { isApiSuccess, shouldUseLocalFallback } from '@/utils/invoice-template-local-store'
import { resolveInvoiceApiUrl } from '@/utils/invoice-api-url'

const API_URL = resolveInvoiceApiUrl()

/** Soft-fail timeout — slightly above 8s so slow hosts still finish; never blocks create. */
const DRAFT_TIMEOUT_MS = 12000

/** One soft warning per session when draft host is unreachable. */
let draftNetworkSoftWarned = false

function softWarnDraftNetwork(prefix, err) {
  const msg = (err && err.message) || String(err || 'unknown error')
  const isNetwork = /timeout|Network Error|ECONNABORTED|ERR_NETWORK|ENOTFOUND|ECONNREFUSED|ERR_CONNECTION/i.test(msg)
    || (err && (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK'))
  if (isNetwork) {
    if (draftNetworkSoftWarned) return
    draftNetworkSoftWarned = true
    // Soft-fail only — wizard continues with session memory.
    console.info(`${prefix} unreachable (soft-fail):`, msg)
    return
  }
  console.warn(`${prefix}:`, msg)
}

/** After first network/timeout failure, skip further draft HTTP until a success resets the flag. */
function sessionDraftFallback(message) {
  return {
    status: 'OK',
    data: session.draft,
    _source: 'session',
    message: message
      || 'Draft API unavailable — kept in memory for this session only.',
  }
}

/** Legacy browser key — read once for migration, then removed. Never written. */
export const LEGACY_INVOICE_DRAFT_KEY = 'invoice_draft_v1'

/** @type {{ draft: object|null, apiUnavailable: boolean }} */
const session = {
  draft: null,
  apiUnavailable: false,
}

function draftPath(suffix = '') {
  return `${API_URL}invoice/drafts${suffix}`
}

/**
 * Identity the backend draft validators expect (query + body).
 * Ownership must still be derived from the auth token server-side;
 * client sends these so half-implemented validators do not 400.
 */
function getDraftCallerIdentity() {
  try {
    const user = JSON.parse(localStorage.getItem('userData') || 'null') || {}
    const empId = user.emp_id || user.empId || null
    const userId = user.id || user.user_id || user.userId || empId || null
    const company = user.company || user.company_name || user.companyName || null
    const companyId = user.company_id || user.companyId || null
    return {
      user_id: userId != null && String(userId).trim() !== '' ? String(userId) : null,
      emp_id: empId != null && String(empId).trim() !== '' ? String(empId) : null,
      company: company != null && String(company).trim() !== '' ? String(company) : null,
      company_id: companyId != null && String(companyId).trim() !== '' ? String(companyId) : null,
    }
  } catch {
    return { user_id: null, emp_id: null, company: null, company_id: null }
  }
}

/** Only numeric DB template ids belong in `template_id` (FK). Built-in string presets do not. */
function coerceDbTemplateId(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const trimmed = String(value).trim()
  if (!trimmed) return null
  const asNum = Number(trimmed)
  if (Number.isFinite(asNum) && String(asNum) === trimmed) return asNum
  return null
}

function normalizeDraftPayload(payload) {
  const body = payload && typeof payload === 'object' ? payload : {}
  const rawTemplateId = body.template_id ?? body.activeTemplateId ?? null
  const dbTemplateId = coerceDbTemplateId(rawTemplateId)
  // Built-in presets use string ids (`standard`, `rpo`, …). Keep them on
  // preset/manual fields — never as a fake numeric template FK.
  const stringPresetId = dbTemplateId == null && rawTemplateId != null && String(rawTemplateId).trim() !== ''
    ? String(rawTemplateId)
    : null

  return {
    formData: body.formData || {},
    lineItems: Array.isArray(body.lineItems) ? body.lineItems : [],
    template_id: dbTemplateId,
    manual_template_id: body.manual_template_id ?? body.manualTemplateId ?? stringPresetId ?? null,
    layout_style: body.layout_style ?? body.previewStyleOverride ?? null,
    line_item_preset_id: body.line_item_preset_id ?? body.lineItemPresetId ?? stringPresetId ?? null,
    line_item_column_config: body.line_item_column_config ?? body.dynamicColumnConfig ?? null,
    line_item_column_config_table2: body.line_item_column_config_table2 ?? body.dynamicColumnConfigTable2 ?? null,
    selected_candidates: body.selected_candidates ?? body.selectedCandidates ?? [],
    is_for_candidates: body.is_for_candidates ?? body.isForCandidates ?? null,
    savedAt: body.savedAt || new Date().toISOString(),
  }
}

/**
 * PUT body: identity fields + flat draft fields + nested `draft` envelope.
 * Backends may validate either shape; extras must be ignored once implemented.
 */
function buildPutBody(payload) {
  const identity = getDraftCallerIdentity()
  const draft = normalizeDraftPayload(payload)
  return {
    ...identity,
    ...draft,
    draft,
  }
}

function buildDraftRequestConfig(extra = {}) {
  const identity = getDraftCallerIdentity()
  const params = { ...(extra.params || {}) }
  if (identity.user_id) params.user_id = identity.user_id
  if (identity.emp_id) params.emp_id = identity.emp_id
  if (identity.company) params.company = identity.company
  if (identity.company_id) params.company_id = identity.company_id
  return {
    timeout: DRAFT_TIMEOUT_MS,
    ...extra,
    params,
    headers: AuthService.authHeader(),
  }
}

/**
 * Draft routes that exist but are incomplete often return 400 (missing
 * company/user_id, strict schema, string template_id) instead of 404/501.
 * Treat those like "API unavailable" so the wizard keeps working.
 */
function shouldUseDraftFallback(err, res) {
  if (shouldUseLocalFallback(err, res)) return true
  // Timeouts / connection failures have no HTTP status — soft-fail to session.
  if (err && !err.response) return true
  const code = err && err.response && err.response.status
  return code === 400 || code === 405 || code === 422
}

function hasMeaningfulDraft(draft) {
  if (!draft || !draft.formData) return false
  return !!(draft.formData.client || (Array.isArray(draft.lineItems) && draft.lineItems.length))
}

class InvoiceDraftService {
  get apiUnavailable() {
    return session.apiUnavailable
  }

  /**
   * One-time read of the old `invoice_draft_v1` key for migration into the
   * API / session store. Removes the key immediately so it cannot remain
   * a standing source of truth.
   */
  consumeLegacyLocalDraft() {
    try {
      const raw = localStorage.getItem(LEGACY_INVOICE_DRAFT_KEY)
      localStorage.removeItem(LEGACY_INVOICE_DRAFT_KEY)
      if (!raw) return null
      const draft = JSON.parse(raw)
      return hasMeaningfulDraft(draft) ? normalizeDraftPayload(draft) : null
    } catch {
      try { localStorage.removeItem(LEGACY_INVOICE_DRAFT_KEY) } catch (e) { /* ignore */ }
      return null
    }
  }

  /** Drop leftover browser keys that must not remain sources of truth. */
  clearLegacyLocalDraft() {
    try {
      localStorage.removeItem(LEGACY_INVOICE_DRAFT_KEY)
      localStorage.removeItem('crm_invoice_templates_v1')
      localStorage.removeItem('crm_invoice_last_line_items_preset')
    } catch (e) { /* ignore */ }
  }

  /**
   * Load the current user's create-invoice draft.
   * @returns {Promise<{ status: string, data: object|null, _source?: string, message?: string }>}
   */
  async getCurrentDraft() {
    // Avoid repeat timeouts / console noise after the draft host already soft-failed.
    if (session.apiUnavailable) {
      return {
        status: 'OK',
        data: hasMeaningfulDraft(session.draft) ? session.draft : null,
        _source: 'session',
      }
    }
    try {
      const res = await axios.get(draftPath('/current'), buildDraftRequestConfig())
      if (isApiSuccess(res.data)) {
        session.apiUnavailable = false
        const raw = res.data.data
        const unwrapped = raw && raw.draft && typeof raw.draft === 'object' ? { ...raw, ...raw.draft } : raw
        const data = unwrapped ? normalizeDraftPayload(unwrapped) : null
        session.draft = hasMeaningfulDraft(data) ? data : null
        return { status: 'OK', data: session.draft, _source: 'api' }
      }
      if (res.data && res.data.status === 'NOT_IMPLEMENTED') {
        throw Object.assign(new Error('NOT_IMPLEMENTED'), { response: res })
      }
    } catch (err) {
      softWarnDraftNetwork('[invoice-draft] getCurrentDraft', err)
      if (!shouldUseDraftFallback(err, err.response && err.response.data)) {
        return {
          status: 'ERROR',
          data: null,
          message: (err.response && err.response.data && err.response.data.message)
            || 'Failed to load invoice draft.',
        }
      }
      session.apiUnavailable = true
    }

    if (hasMeaningfulDraft(session.draft)) {
      return { status: 'OK', data: session.draft, _source: 'session' }
    }
    return { status: 'OK', data: null, _source: session.apiUnavailable ? 'session' : 'api' }
  }

  /**
   * Upsert the current user's draft (debounced callers use this).
   * @returns {Promise<{ status: string, data?: object, _source?: string, message?: string }>}
   */
  async saveCurrentDraft(payload) {
    const draft = normalizeDraftPayload(payload)
    if (!hasMeaningfulDraft(draft)) {
      return { status: 'OK', data: null, message: 'Empty draft skipped.' }
    }

    // Keep wizard responsive: after timeout/network soft-fail, only update session memory.
    if (session.apiUnavailable) {
      session.draft = draft
      return sessionDraftFallback(
        'Draft API unavailable — kept in memory for this session only. Backend draft persistence is required.',
      )
    }

    const body = buildPutBody(payload)

    try {
      const res = await axios.put(draftPath('/current'), body, buildDraftRequestConfig())
      if (isApiSuccess(res.data)) {
        session.apiUnavailable = false
        const raw = res.data.data || draft
        const unwrapped = raw && raw.draft && typeof raw.draft === 'object' ? { ...raw, ...raw.draft } : raw
        session.draft = normalizeDraftPayload(unwrapped)
        this.clearLegacyLocalDraft()
        return { status: 'OK', data: session.draft, _source: 'api' }
      }
      if (res.data && res.data.status === 'NOT_IMPLEMENTED') {
        throw Object.assign(new Error('NOT_IMPLEMENTED'), { response: res })
      }
      // Non-OK JSON body without throw — treat like incomplete API.
      session.apiUnavailable = true
      session.draft = draft
      return {
        status: 'OK',
        data: draft,
        _source: 'session',
        message: (res.data && res.data.message)
          || 'Draft API rejected the save — kept in memory for this session only.',
      }
    } catch (err) {
      softWarnDraftNetwork('[invoice-draft] saveCurrentDraft', err)
      if (!shouldUseDraftFallback(err, err.response && err.response.data)) {
        return {
          status: 'ERROR',
          message: (err.response && err.response.data && err.response.data.message)
            || 'Failed to save invoice draft.',
        }
      }
      session.apiUnavailable = true
      session.draft = draft
      return {
        status: 'OK',
        data: draft,
        _source: 'session',
        message: 'Draft API unavailable — kept in memory for this session only. Backend draft persistence is required.',
      }
    }
  }

  /**
   * Delete the current user's draft (after successful invoice create or Discard).
   */
  async deleteCurrentDraft() {
    session.draft = null
    this.clearLegacyLocalDraft()
    if (session.apiUnavailable) {
      return {
        status: 'OK',
        message: 'Draft cleared for this session.',
        _source: 'session',
      }
    }
    try {
      const res = await axios.delete(draftPath('/current'), buildDraftRequestConfig())
      if (isApiSuccess(res.data) || (res.data && res.data.status === 'OK')) {
        session.apiUnavailable = false
        return { status: 'OK', message: 'Draft deleted.', _source: 'api' }
      }
      if (res.data && res.data.status === 'NOT_IMPLEMENTED') {
        throw Object.assign(new Error('NOT_IMPLEMENTED'), { response: res })
      }
    } catch (err) {
      softWarnDraftNetwork('[invoice-draft] deleteCurrentDraft', err)
      if (!shouldUseDraftFallback(err, err.response && err.response.data)) {
        return {
          status: 'ERROR',
          message: (err.response && err.response.data && err.response.data.message)
            || 'Failed to delete invoice draft.',
        }
      }
      session.apiUnavailable = true
    }
    return {
      status: 'OK',
      message: 'Draft cleared for this session.',
      _source: session.apiUnavailable ? 'session' : 'api',
    }
  }
}

export default new InvoiceDraftService()
